import { Camera, FileText, ShieldCheck, TrendingUp } from 'lucide-react';
import type React from 'react';
import { cn } from '@/infra/utils';

export const CORE_UI_TOKENS = {
  background: 'bg-[#0b0d10]',
  card: 'bg-[#15191f]',
  cardDark: 'bg-[#0f1217]',
  border: 'border-[#2a2f38]',
  mutedText: 'text-slate-300',
  success: 'text-[#00e676]',
  successBg: 'bg-[#00e676]/10',
  amber: 'text-amber-300',
  amberBg: 'bg-amber-500/10',
  risk: 'text-[#ef4444]',
  riskBg: 'bg-[#ef4444]/10',
  info: 'text-cyan-300',
  infoBg: 'bg-cyan-500/10',
} as const;

export type CoreStatusBadgeTone = 'success' | 'amber' | 'risk' | 'info' | 'muted';

export type CoreStatusBadgeLabel =
  | 'Zdjęcie: kontekst'
  | 'Profit'
  | 'PDF'
  | 'Do potwierdzenia'
  | 'Liczenie lokalne'
  | 'Gotowe do PDF'
  | 'Ryzykowne'
  | 'Opłacalne';

const BADGE_STYLES: Record<CoreStatusBadgeTone, string> = {
  success: 'border-[#00e676]/35 bg-[#00e676]/10 text-[#00e676]',
  amber: 'border-amber-400/40 bg-amber-500/10 text-amber-200',
  risk: 'border-[#ef4444]/40 bg-[#ef4444]/10 text-red-200',
  info: 'border-cyan-400/35 bg-cyan-500/10 text-cyan-200',
  muted: 'border-[#2a2f38] bg-[#0f1217] text-slate-300',
};

const BADGE_ICONS: Partial<Record<CoreStatusBadgeLabel, React.ComponentType<{ className?: string }>>> = {
  'Zdjęcie: kontekst': Camera,
  Profit: TrendingUp,
  PDF: FileText,
  'Do potwierdzenia': ShieldCheck,
  'Liczenie lokalne': ShieldCheck,
  'Gotowe do PDF': FileText,
  Ryzykowne: TrendingUp,
  Opłacalne: TrendingUp,
};

export function CoreStatusBadge({
  label,
  tone = 'muted',
  className,
}: {
  label: CoreStatusBadgeLabel | string;
  tone?: CoreStatusBadgeTone;
  className?: string;
}) {
  const Icon = BADGE_ICONS[label as CoreStatusBadgeLabel];

  return (
    <span
      className={cn(
        'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-black uppercase tracking-wider',
        BADGE_STYLES[tone],
        className
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

export function CoreStatusBadgeRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}
    </div>
  );
}
